import { downloadFileById } from "@api";
import {
  useGetConsultantBookingDocumentListQuery,
  useGetConsultantDocumentListQuery,
  useRemoveConsultantDocumentMutation
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { NodeType } from "@bonliva-crm/shared";
import {
  ChevronRightIcon,
  Color,
  DeleteIcon,
  DownloadIcon,
  DraftIcon,
  Loader,
  OpenInNewIcon
} from "@bonliva-crm/ui";
import { useConfirmDialog, useSnackbars } from "@hooks";
import {
  Collapse,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import cns from "classnames";
import { useMemo, useState } from "react";
import { useStyles } from "../styles";
import { OkCancelDialogOptions } from "@containers";

const DOCUMENT_GROUPS: Record<string, string[]> = {
  "IVO presentation": ["IVO", "IVO-Grund", "IVO-Ärenden"]
};

type DocumentItem = {
  DocumentType?: string | null;
  ThumbURL?: string;
  PdfURL?: string;
  booking?: boolean;
  FileRef: {
    MimeType: string;
    Name?: string | null;
    NodeID: string;
    NodeType: NodeType;
    Size: number;
  };
};

type Props = {
  consultantId: string;
};

export function FileList({ consultantId }: Props) {
  const classes = useStyles();
  const [openFields, setOpenFields] = useState<string[]>([]);
  const { showInfo, showError } = useSnackbars();

  const consultantDocumentListQuery = useGetConsultantDocumentListQuery({
    variables: { id: consultantId },
    fetchPolicy: "cache-and-network"
  });

  const consultantBookingDocumentsQuery = useGetConsultantBookingDocumentListQuery({
    variables: { id: consultantId },
    fetchPolicy: "cache-and-network"
  });

  const [removeDocument] = useRemoveConsultantDocumentMutation({
    onCompleted: () => {
      consultantDocumentListQuery.refetch();
      consultantBookingDocumentsQuery.refetch();
    }
  });

  const options = useMemo(() => new OkCancelDialogOptions(locale.RemoveFile), []);

  const confirmDialog = useConfirmDialog(options);

  const handleRemove = (documentId: string) => {
    confirmDialog.confirm().then(async (value) => {
      if (value === "ok") {
        await removeDocument({
          variables: { documentId }
        });
      }
    });
  };

  const sortByDocumentType = () => {
    const consultantFiles = consultantDocumentListQuery.data?.docs?.Documents || [];
    const bookingFiles =
      consultantBookingDocumentsQuery.data?.consultantById.BookingDocuments || [];
    const sortedFiles: Record<string, DocumentItem[]> = {};

    consultantFiles.forEach((file) => {
      if (!sortedFiles[file.DocumentType]) {
        sortedFiles[file.DocumentType] = [file];
      } else {
        sortedFiles[file.DocumentType].push(file);
      }
    });

    bookingFiles.forEach((file) => {
      const fileType =
        locale.bookingWeekConsultantDocumentWithOutBookingType[file.DocumentType] ??
        locale.bookingWeekDocumentTypes[file.DocumentType];

      if (!sortedFiles[fileType]) {
        sortedFiles[fileType] = [
          {
            ...file,
            booking: true
          }
        ];
      } else {
        sortedFiles[fileType].push({
          ...file,
          booking: true
        });
      }
    });

    const groupedFiles: Record<string, DocumentItem[] | Record<string, DocumentItem[]>> =
      {};

    Object.entries(sortedFiles).forEach(([category, files]) => {
      let foundGroup = false;
      for (const [groupName, categories] of Object.entries(DOCUMENT_GROUPS)) {
        if (categories.includes(category)) {
          if (!groupedFiles[groupName]) {
            groupedFiles[groupName] = {};
          }
          (groupedFiles[groupName] as Record<string, DocumentItem[]>)[category] = files;
          foundGroup = true;
          break;
        }
      }

      if (!foundGroup) {
        groupedFiles[category] = files;
      }
    });

    return groupedFiles;
  };

  const toggleOpen = (key: string) => {
    if (openFields.includes(key)) {
      setOpenFields(openFields.filter((k) => k !== key));
    } else {
      setOpenFields([...openFields, key]);
    }
  };

  if (
    (consultantDocumentListQuery.loading && !consultantDocumentListQuery.data) ||
    (consultantBookingDocumentsQuery.loading && !consultantBookingDocumentsQuery.data)
  ) {
    return (
      <Paper
        className={cns(classes.fileListWrapper, classes.borderRadius2, classes.padding20)}
      >
        <Loader />
      </Paper>
    );
  }

  return (
    <Paper
      className={cns(classes.fileListWrapper, classes.borderRadius2, classes.padding20)}
    >
      <div className={classes.topWrapper}>
        <Typography variant="h5">{locale.UploadedFiles}</Typography>
        <Typography variant="body2">
          {locale.UploadedFilesShownHere}{" "}
          <Link
            onClick={() => showInfo(locale.NotImplemented)}
            className={classes.pointer}
          >
            {locale.Here}
          </Link>
          .
        </Typography>
      </div>

      <List className={classes.listWrapper}>
        {Object.entries(sortByDocumentType())
          .sortBy(([k]) => k)
          .map(([key, content], i) => (
            <div className={classes.itemWrapper} key={i}>
              <ListItem className={classes.width100} onClick={() => toggleOpen(key)}>
                <ListItemText primary={key} />
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => toggleOpen(key)}
                >
                  {openFields.includes(key) ? (
                    <ChevronRightIcon style={{ transform: "rotate(-90deg)" }} />
                  ) : (
                    <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
                  )}
                </IconButton>
              </ListItem>
              <Collapse in={openFields.includes(key)} unmountOnExit>
                {Array.isArray(content) ? (
                  <List className={cns(classes.flexCenterColumn, classes.padding20)}>
                    {content.map((file, i) => (
                      <ListItem className={classes.fileListItem} key={i}>
                        <div className={classes.fileListTextWrapper}>
                          <DraftIcon fontSize="small" />
                          <Tooltip title={file.FileRef.Name} placement="top">
                            <Typography
                              variant="body2"
                              className={cns(classes.textEllipsis)}
                            >
                              {file.FileRef.Name}
                            </Typography>
                          </Tooltip>
                        </div>

                        <div className={classes.flexCenter}>
                          {!file.booking && (
                            <DeleteIcon
                              className={classes.pointer}
                              fontSize="small"
                              htmlColor={Color.PrimaryMain}
                              onClick={() => handleRemove(file.FileRef.NodeID)}
                            />
                          )}

                          <DownloadIcon
                            onClick={() =>
                              downloadFileById(
                                file.FileRef.NodeID,
                                file.FileRef.Name
                              ).catch((e) => {
                                showError(locale.DownloadFailed);
                                console.error(e);
                              })
                            }
                            className={classes.pointer}
                            fontSize="small"
                            htmlColor={Color.PrimaryMain}
                          />

                          <OpenInNewIcon
                            onClick={() =>
                              window.open(
                                `/shared-files/${file.FileRef.NodeID}`,
                                "_blank"
                              )
                            }
                            className={classes.pointer}
                            fontSize="small"
                            htmlColor={Color.PrimaryMain}
                          />
                        </div>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <List className={cns(classes.width100, classes.padding20)}>
                    {Object.entries(content)
                      .sortBy(([k]) => k)
                      .map(([subKey, files], j) => (
                        <div key={j} className={classes.documentTypeWrapper}>
                          <ListItem
                            className={classes.width100}
                            onClick={() => toggleOpen(subKey)}
                          >
                            <ListItemText primary={subKey} />
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => toggleOpen(subKey)}
                            >
                              {openFields.includes(subKey) ? (
                                <ChevronRightIcon
                                  style={{ transform: "rotate(-90deg)" }}
                                />
                              ) : (
                                <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
                              )}
                            </IconButton>
                          </ListItem>
                          <Collapse in={openFields.includes(subKey)} unmountOnExit>
                            <List
                              className={cns(classes.flexCenterColumn, classes.padding20)}
                            >
                              {files.map((file, k) => (
                                <ListItem className={classes.fileListItem} key={k}>
                                  <div className={classes.fileListTextWrapper}>
                                    <DraftIcon fontSize="small" />
                                    <Tooltip title={file.FileRef.Name} placement="top">
                                      <Typography
                                        variant="body2"
                                        className={cns(classes.textEllipsis)}
                                      >
                                        {file.FileRef.Name}
                                      </Typography>
                                    </Tooltip>
                                  </div>

                                  <div className={classes.flexCenter}>
                                    {!file.booking && (
                                      <DeleteIcon
                                        className={classes.pointer}
                                        fontSize="small"
                                        htmlColor={Color.PrimaryMain}
                                        onClick={() => handleRemove(file.FileRef.NodeID)}
                                      />
                                    )}

                                    <DownloadIcon
                                      onClick={() =>
                                        downloadFileById(
                                          file.FileRef.NodeID,
                                          file.FileRef.Name
                                        ).catch((e) => {
                                          showError(locale.DownloadFailed);
                                          console.error(e);
                                        })
                                      }
                                      className={classes.pointer}
                                      fontSize="small"
                                      htmlColor={Color.PrimaryMain}
                                    />

                                    <OpenInNewIcon
                                      onClick={() =>
                                        window.open(
                                          `/shared-files/${file.FileRef.NodeID}`,
                                          "_blank"
                                        )
                                      }
                                      className={classes.pointer}
                                      fontSize="small"
                                      htmlColor={Color.PrimaryMain}
                                    />
                                  </div>
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </div>
                      ))}
                  </List>
                )}
              </Collapse>
            </div>
          ))}
      </List>
    </Paper>
  );
}
